import { useMutation } from '@apollo/client';
import { t } from 'i18next';
import React, { useState } from 'react'
import { MdClose } from 'react-icons/md';
import { ALL_COUPONS, CreateCoupon } from '../../graphql';
import FailAlert from '../../components/Alerts/FailAlert';
import useStore from '../../store/useStore';

interface AddAlertPopupProps {
  setAddCouponPopup: React.Dispatch<React.SetStateAction<boolean>>,
  setSuccessMsg: React.Dispatch<React.SetStateAction<string>>,
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>,
}

const AddCoupon = ({ setAddCouponPopup, setSuccessMsg, setSuccess }: AddAlertPopupProps) => {
  const storeData = useStore((state: any) => state.userData.store);

  const [createCoupon, { error, loading }] = useMutation(CreateCoupon, {
    refetchQueries: [{
      query: ALL_COUPONS,
      variables: { idStore: storeData.id }
    }]
  });

  const [fail, setFail] = useState(false);
  const [failMsg, setFailMsg] = useState("");

  const [name, setName] = useState("");
  const [value, setValue] = useState('');
  const [date, setDate] = useState('');
  const [minimumpurchases, setMinimumPurchases] = useState('');
  const [totalUse, setTotalUse] = useState('');
  const [totalUsePerCustomer, setTotalUsePerCustomer] = useState('');

  const submit = async (e: any) => {
    e.preventDefault();

    if (name === "") {
      setFailMsg(t('emptyNameAlert'));
      setFail(true);
      return
    }

    if (value === "") {
      setFailMsg(t('emptyValueAlert'));
      setFail(true);
      return
    }

    if (date === "") {
      setFailMsg(t('emptyDateAlert'));
      setFail(true);
      return
    }

    if (minimumpurchases === "") {
      setFailMsg(t('minimumpurchasesAlert'));
      setFail(true);
      return
    }

    if (totalUse === "") {
      setFailMsg(t('totalUseAlert'));
      setFail(true);
      return
    }

    if (totalUsePerCustomer === "") {
      setFailMsg(t('totalUsePerCustomerAlert'));
      setFail(true);
      return
    }

    await createCoupon({
      variables: {
        content: {
          expiryDate: date,
          name: name,
          idStore: storeData.id,
          minimumPurchases: parseInt(minimumpurchases),
          numberTimesUsedPerCustomer: parseInt(totalUsePerCustomer),
          totalNumberTimesUsed: parseInt(totalUse),
          value: parseFloat(value)
        }
      }
    })

    if (!error) {
      setSuccessMsg(t('couponAddSucc'));
      setSuccess(true);
      setAddCouponPopup(false)
    }
  }

  return (
    <div className='absolute top-0 left-0 z-50 flex items-center justify-center w-full min-h-screen bg-gray-900/10 backdrop-blur-md'>
      {fail && <FailAlert fail={fail} setFail={setFail} failMsg={failMsg} />}

      <div className='relative w-5/6 p-2 my-6 bg-white rounded-md sm:w-4/6 md:w-3/6'>
        {
          loading &&
          <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
            <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
            </svg>
          </div>
        }
        <div className='flex justify-end text-black cursor-pointer' onClick={() => { setAddCouponPopup(false) }}>
          <MdClose size={20} />
        </div>

        <form className='px-5 py-2 my-2 bg-white'>
          <div className="mb-4">
            <label htmlFor="copoun" className="block mb-2 text-sm font-medium text-gray-900">{t('coupon')}</label>
            <input type="text" id="copoun" value={name} onChange={(e) => setName(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder='new20' required />
          </div>

          <div className="mb-4">
            <input type="number" min={0} value={value} onChange={(e) => setValue(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('value')} required />
          </div>

          <div className="mb-4">
            <label htmlFor="endDate" className="block mb-2 text-sm font-medium text-gray-900">{t('Discount End Date')}</label>
            <input type="date" id="endDate" value={date} onChange={(e) => setDate(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('Discount End Date')} required />
          </div>

          <div className="mb-4">
            <input type="number" value={minimumpurchases} onChange={(e) => setMinimumPurchases(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('Minimum purchases')} required />
          </div>

          <div className="mb-4">
            <input type="number" min={0} value={totalUse} onChange={(e) => setTotalUse(e.target.value)} id="value" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('Number of times of total use')} required />
          </div>

          <div className="mb-4">
            <input type="number" min={0} id="value" value={totalUsePerCustomer} onChange={(e) => setTotalUsePerCustomer(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('Number of uses per customer')} required />
          </div>

          <div className='flex justify-end pt-5'>
            <button onClick={(e) => { submit(e) }} className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
              </svg>

              <span className="mx-2">
                {t('Add Coupon')}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div >
  )
}

export default AddCoupon